// 🔧 scheduler.js
// ✅ Purpose: Event Setup Scheduler page for managing public event activity schedules
// ✅ Features:
// - Allows managers/admins to set how many days before the event the public schedule appears
// - Allows unlimited schedule items per event
// - Supports multi-day event schedules using a schedule date field
// - Supports required start time and optional end time
// - If end time is blank, public schedule displays "9:00 AM - end"
// - Saves schedule visibility setting directly on the event document as scheduleShowDaysBefore
// - Saves schedule items under /events/{eventId}/schedule_items
// - Sorts schedule items by schedule date and start time
// - Shows success and error notifications at the top of the page for 20 seconds
// - Uses console logging through logger to help sniff out issues during testing
// - Fully block-commented for clarity and future devs

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    query,
    serverTimestamp,
    updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";

const Scheduler = () => {
    const {
        eventId,
        eventDoc,
        eventLoading,
        ready,
    } = useEventContext({ redirectIfNotFound: true });

    const [scheduleShowDaysBefore, setScheduleShowDaysBefore] = useState(7);
    const [scheduleItems, setScheduleItems] = useState([]);
    const [loadingScheduleItems, setLoadingScheduleItems] = useState(true);
    const [savingSetting, setSavingSetting] = useState(false);
    const [savingItem, setSavingItem] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);
    const [status, setStatus] = useState(null);

    const [form, setForm] = useState({
        title: "",
        description: "",
        location: "",
        scheduleDate: "",
        startTime: "",
        endTime: "",
    });

    const showStatusMessage = useCallback((type, message) => {
        setStatus({ type, message });

        setTimeout(() => {
            setStatus(null);
        }, 20000);
    }, []);

    const getEventDateValue = useCallback((value) => {
        if (!value) return null;

        if (value?.toDate) {
            return value.toDate();
        }

        const parsed = new Date(value);

        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }, []);

    const defaultScheduleDate = useMemo(() => {
        const eventStartDate = getEventDateValue(eventDoc?.startDate);

        if (!eventStartDate) return "";

        return eventStartDate.toISOString().slice(0, 10);
    }, [eventDoc?.startDate, getEventDateValue]);

    const resetForm = useCallback(() => {
        setEditingItemId(null);

        setForm({
            title: "",
            description: "",
            location: "",
            scheduleDate: defaultScheduleDate,
            startTime: "",
            endTime: "",
        });
    }, [defaultScheduleDate]);

    const calculateStartMinutes = (timeValue) => {
        if (!timeValue || !timeValue.includes(":")) return 0;

        const [hours, minutes] = timeValue.split(":").map(Number);

        return hours * 60 + minutes;
    };

    const formatScheduleTime = (timeValue) => {
        if (!timeValue) return "";

        const [hours, minutes] = timeValue.split(":").map(Number);

        const date = new Date();

        date.setHours(hours, minutes, 0, 0);

        return date.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const getScheduleTimeLabel = (item) => {
        const startLabel = formatScheduleTime(item.startTime);

        if (!item.endTime) {
            return `${startLabel} - end`;
        }

        return `${startLabel} - ${formatScheduleTime(item.endTime)}`;
    };

    const formatScheduleDate = (dateValue) => {
        if (!dateValue) return "Schedule Date Not Set";

        return new Date(`${dateValue}T00:00:00`).toLocaleDateString([], {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    };

    const loadScheduleItems = useCallback(async () => {
        if (!eventId) return;

        setLoadingScheduleItems(true);

        try {
            logger.log("📅 Loading event schedule items", {
                eventId,
            });

            const scheduleItemsRef = collection(
                db,
                "events",
                eventId,
                "schedule_items"
            );

            const scheduleItemsQuery = query(scheduleItemsRef);

            const snap = await getDocs(scheduleItemsQuery);

            const items = snap.docs
                .map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }))
                .sort((a, b) => {
                    const dateCompare = String(a.scheduleDate || "").localeCompare(
                        String(b.scheduleDate || "")
                    );

                    if (dateCompare !== 0) return dateCompare;

                    return Number(a.sortStartMinutes || 0) - Number(b.sortStartMinutes || 0);
                });

            setScheduleItems(items);

            logger.log("✅ Event schedule items loaded", {
                eventId,
                count: items.length,
            });
        } catch (err) {
            logger.error("❌ Error loading event schedule items:", {
                value: err,
            });

            setScheduleItems([]);

            showStatusMessage(
                "error",
                "Unable to load schedule items. Please check permissions and try again."
            );
        } finally {
            setLoadingScheduleItems(false);
        }
    }, [eventId, showStatusMessage]);

    useEffect(() => {
        if (!ready || !eventId || eventLoading) return;

        setScheduleShowDaysBefore(
            Number(eventDoc?.scheduleShowDaysBefore ?? 7)
        );

        setForm((previousForm) => ({
            ...previousForm,
            scheduleDate: previousForm.scheduleDate || defaultScheduleDate,
        }));

        loadScheduleItems();
    }, [
        ready,
        eventId,
        eventLoading,
        eventDoc?.scheduleShowDaysBefore,
        defaultScheduleDate,
        loadScheduleItems,
    ]);

    const handleSaveVisibilitySetting = async () => {
        if (!eventId) return;

        const numericDaysBefore = Number(scheduleShowDaysBefore);

        if (!Number.isFinite(numericDaysBefore) || numericDaysBefore < 0) {
            showStatusMessage(
                "warning",
                "Schedule Visibility must be 0 or greater."
            );

            return;
        }

        setSavingSetting(true);

        try {
            logger.log("💾 Saving schedule visibility setting", {
                eventId,
                scheduleShowDaysBefore: numericDaysBefore,
            });

            await updateDoc(doc(db, "events", eventId), {
                scheduleShowDaysBefore: numericDaysBefore,
                updatedAt: serverTimestamp(),
            });

            showStatusMessage(
                "success",
                "Schedule visibility setting saved."
            );
        } catch (err) {
            logger.error("❌ Error saving schedule visibility setting:", {
                value: err,
            });

            showStatusMessage(
                "error",
                "Unable to save schedule visibility setting."
            );
        } finally {
            setSavingSetting(false);
        }
    };

    const handleEditScheduleItem = (item) => {
        setEditingItemId(item.id);

        setForm({
            title: item.title || "",
            description: item.description || "",
            location: item.location || "",
            scheduleDate: item.scheduleDate || defaultScheduleDate,
            startTime: item.startTime || "",
            endTime: item.endTime || "",
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleDeleteScheduleItem = async (itemId) => {
        if (!eventId || !itemId) return;

        const confirmed = window.confirm(
            "Delete this schedule item? This cannot be undone."
        );

        if (!confirmed) return;

        try {
            logger.log("🗑️ Deleting schedule item", {
                eventId,
                itemId,
            });

            await deleteDoc(
                doc(db, "events", eventId, "schedule_items", itemId)
            );

            showStatusMessage(
                "success",
                "Schedule item deleted."
            );

            loadScheduleItems();
        } catch (err) {
            logger.error("❌ Error deleting schedule item:", {
                value: err,
            });

            showStatusMessage(
                "error",
                "Unable to delete schedule item."
            );
        }
    };

    const handleSaveScheduleItem = async (e) => {
        e.preventDefault();

        if (!eventId) return;

        const title = form.title.trim();
        const description = form.description.trim();
        const location = form.location.trim();
        const scheduleDate = form.scheduleDate;
        const startTime = form.startTime;
        const endTime = form.endTime;

        if (!title || !scheduleDate || !startTime) {
            showStatusMessage(
                "warning",
                "Title, schedule date, and start time are required."
            );

            return;
        }

        if (endTime && endTime < startTime) {
            showStatusMessage(
                "warning",
                "End time cannot be before the start time."
            );

            return;
        }

        setSavingItem(true);

        try {
            const schedulePayload = {
                title,
                description,
                location,
                scheduleDate,
                startTime,
                endTime,
                sortDateTime: `${scheduleDate}T${startTime}`,
                sortStartMinutes: calculateStartMinutes(startTime),
                updatedAt: serverTimestamp(),
            };

            if (editingItemId) {
                logger.log("💾 Updating schedule item", {
                    eventId,
                    editingItemId,
                    schedulePayload,
                });

                await updateDoc(
                    doc(db, "events", eventId, "schedule_items", editingItemId),
                    schedulePayload
                );

                showStatusMessage(
                    "success",
                    "Schedule item updated."
                );
            } else {
                logger.log("➕ Creating schedule item", {
                    eventId,
                    schedulePayload,
                });

                await addDoc(
                    collection(db, "events", eventId, "schedule_items"),
                    {
                        ...schedulePayload,
                        createdAt: serverTimestamp(),
                    }
                );

                showStatusMessage(
                    "success",
                    "Schedule item added."
                );
            }

            resetForm();
            loadScheduleItems();
        } catch (err) {
            logger.error("❌ Error saving schedule item:", {
                value: err,
            });

            showStatusMessage(
                "error",
                "Unable to save schedule item."
            );
        } finally {
            setSavingItem(false);
        }
    };

    const scheduleItemsByDate = scheduleItems.reduce((groups, item) => {
        const dateKey = item.scheduleDate || "unscheduled";

        if (!groups[dateKey]) {
            groups[dateKey] = [];
        }

        groups[dateKey].push(item);

        return groups;
    }, {});

    if (!ready || !eventId || eventLoading) {
        return <FullPageSpinner message="Loading Scheduler..." />;
    }

    return (
        <section id="top-content" className="container-fluid py-5">
            <div className="container" style={{ maxWidth: "1100px" }}>
                {status?.message && (
                    <div
                        className={`alert ${
                            status.type === "success"
                                ? "alert-success"
                                : status.type === "warning"
                                ? "alert-warning"
                                : status.type === "error"
                                ? "alert-danger"
                                : "alert-info"
                        }`}
                    >
                        {status.message}
                    </div>
                )}

                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-white">
                        <strong>👀 Schedule Visibility</strong>
                    </div>

                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-6">
                                <label className="form-label">
                                    Schedule Visibility Days Before Event
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    value={scheduleShowDaysBefore}
                                    onChange={(e) =>
                                        setScheduleShowDaysBefore(e.target.value)
                                    }
                                />

                                <div className="form-text">
                                    Example: 7 means the public schedule appears 7 days before the event starts.
                                    Use 0 to show the schedule on the event start date.
                                </div>
                            </div>

                            <div className="col-12 col-md-auto">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={savingSetting}
                                    onClick={handleSaveVisibilitySetting}
                                >
                                    {savingSetting ? "Saving..." : "Save Visibility"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-white">
                        <strong>
                            {editingItemId ? "✏️ Edit Schedule Item" : "➕ Add Schedule Item"}
                        </strong>
                    </div>

                    <div className="card-body">
                        <form onSubmit={handleSaveScheduleItem}>
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="form-label">
                                        Activity Title
                                    </label>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={form.title}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                title: e.target.value,
                                            })
                                        }
                                        placeholder="Example: Registration Opens"
                                        required
                                    />
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label">
                                        Schedule Date
                                    </label>

                                    <input
                                        type="date"
                                        className="form-control"
                                        value={form.scheduleDate}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                scheduleDate: e.target.value,
                                            })
                                        }
                                        required
                                    />

                                    <div className="form-text">
                                        Use this for multi-day events.
                                    </div>
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label">
                                        Start Time
                                    </label>

                                    <input
                                        type="time"
                                        className="form-control"
                                        value={form.startTime}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                startTime: e.target.value,
                                            })
                                        }
                                        required
                                    />
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label">
                                        End Time
                                    </label>

                                    <input
                                        type="time"
                                        className="form-control"
                                        value={form.endTime}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                endTime: e.target.value,
                                            })
                                        }
                                    />

                                    <div className="form-text">
                                        Leave blank to show “end” on the public schedule.
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <label className="form-label">
                                        Location
                                    </label>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={form.location}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                location: e.target.value,
                                            })
                                        }
                                        placeholder="Example: Main Stage"
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="form-label">
                                        Description
                                    </label>

                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={form.description}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                description: e.target.value,
                                            })
                                        }
                                        placeholder="Optional details attendees should know."
                                    />
                                </div>

                                <div className="col-12 d-flex flex-column flex-md-row gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={savingItem}
                                    >
                                        {savingItem
                                            ? "Saving..."
                                            : editingItemId
                                            ? "Update Schedule Item"
                                            : "Add Schedule Item"}
                                    </button>

                                    {editingItemId && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={resetForm}
                                            disabled={savingItem}
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                        <div>
                            <strong>📋 Current Schedule</strong>
                            <div className="small text-muted">
                                Items are grouped by date and sorted by start time.
                            </div>
                        </div>

                        <span className="badge bg-dark align-self-start align-self-md-center">
                            {scheduleItems.length} Item{scheduleItems.length === 1 ? "" : "s"}
                        </span>
                    </div>

                    <div className="card-body">
                        {loadingScheduleItems ? (
                            <div className="text-muted">
                                Loading schedule items...
                            </div>
                        ) : scheduleItems.length === 0 ? (
                            <div className="alert alert-info mb-0">
                                No schedule items have been added yet.
                            </div>
                        ) : (
                            Object.entries(scheduleItemsByDate).map(([dateKey, items]) => (
                                <div key={dateKey} className="mb-4">
                                    <h5 className="fw-bold border-bottom pb-2 mb-3">
                                        📅 {formatScheduleDate(dateKey)}
                                    </h5>

                                    <div className="list-group">
                                        {items.map((item) => (
                                            <div
                                                key={item.id}
                                                className="list-group-item"
                                            >
                                                <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                                                    <div>
                                                        <div className="fw-bold">
                                                            {item.title}
                                                        </div>

                                                        <div className="small text-muted">
                                                            🕘 {getScheduleTimeLabel(item)}
                                                        </div>

                                                        {item.location && (
                                                            <div className="small text-muted">
                                                                📍 {item.location}
                                                            </div>
                                                        )}

                                                        {item.description && (
                                                            <div className="small mt-2">
                                                                {item.description}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="d-flex gap-2 align-self-start">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() =>
                                                                handleEditScheduleItem(item)
                                                            }
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() =>
                                                                handleDeleteScheduleItem(item.id)
                                                            }
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Scheduler;